import React, { createContext, useContext, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

interface Tenant {
  id: number;
  name: string;
  website_url: string;
}

interface TenantContextType {
  currentTenant: Tenant | null;
  setCurrentTenant: (tenant: Tenant | null) => void;
}

const TenantContext = createContext<TenantContextType | undefined>(undefined);

export const TenantProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [currentTenant, setCurrentTenant] = useState<Tenant | null>(null);
  const location = useLocation();
  const navigate = useNavigate();

  const handleSetCurrentTenant = (tenant: Tenant | null) => {
    setCurrentTenant(tenant);
    // Update URL to reflect new tenant
    if (tenant) {
      const pathParts = location.pathname.split('/');
      // If we're at the root or login, go to dashboard
      if (pathParts.length <= 2 || pathParts[1] === 'login') {
        navigate(`/${tenant.website_url}/dashboard`);
      } else {
        // Otherwise, just update the tenant part of the path
        pathParts[1] = tenant.website_url;
        navigate(pathParts.join('/'));
      }
    } else {
      // If no tenant, navigate to root
      navigate('/');
    }
  };

  return (
    <TenantContext.Provider value={{ currentTenant, setCurrentTenant: handleSetCurrentTenant }}>
      {children}
    </TenantContext.Provider>
  );
};

export const useTenant = () => {
  const context = useContext(TenantContext);
  if (context === undefined) {
    throw new Error('useTenant must be used within a TenantProvider');
  }
  return context;
};